import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { getUserToken } from "utils/helperFunctions";
import type { RootState } from "utils/redux/store";
import {
  SeniorManagerReportSettings,
  SeniorManagerReportPreview,
  SeniorManagerReportPreviewRecipient,
  TeamLeaderReportSettings,
  TeamLeaderReportPreview,
  TeamLeaderReportPreviewRecipient,
  ReportSummary,
  UpdatePayload,
} from "app/containers/WeeklyCheckInMonitor/types";

/* --------------------------------------------------------
  OPTIMISTIC UPDATE HELPERS
-------------------------------------------------------- */

function optimisticallyExcludeRecipient<
  T extends { userAccountId: number; status: string }
>(recipients: T[], userAccountId: number, mode: string | null): T[] {
  return recipients.map((r) => {
    if (r.userAccountId !== userAccountId) return r;
    if (mode === "specific_people") {
      return {
        ...r,
        status: "not_included",
        overrideCreatedByName: null,
        overrideCreatedAt: null,
        alsoQualifies: false,
      };
    }
    return {
      ...r,
      status: "excluded",
      overrideCreatedByName: null,
      overrideCreatedAt: null,
      alsoQualifies: false,
    };
  });
}

function optimisticallyIncludeRecipient<
  T extends { userAccountId: number; status: string }
>(
  recipients: T[],
  userAccountId: number,
  includedUserIds: Set<number>
): T[] {
  return recipients.map((r) => {
    if (r.userAccountId !== userAccountId) return r;
    // below_threshold = manually adding them above the threshold → included_override
    // excluded + in includedUsers = re-including a manually-added person → included_override
    // excluded + not in includedUsers = re-including a rule-qualified person → qualified_by_rules
    const newStatus =
      r.status === "below_threshold" || includedUserIds.has(r.userAccountId)
        ? "included_override"
        : "qualified_by_rules";
    return {
      ...r,
      status: newStatus,
      overrideCreatedByName: null,
      overrideCreatedAt: null,
      alsoQualifies: false,
    };
  });
}

function recalculateSummary(
  recipients: { status: string }[]
): ReportSummary {
  const summary = {
    qualifiedByRules: 0,
    includedOverride: 0,
    excluded: 0,
    belowThreshold: 0,
    notIncluded: 0,
  };
  for (const r of recipients) {
    if (r.status === "qualified_by_rules") summary.qualifiedByRules++;
    else if (r.status === "included_override") summary.includedOverride++;
    else if (r.status === "excluded") summary.excluded++;
    else if (r.status === "below_threshold") summary.belowThreshold++;
    else if (r.status === "not_included") summary.notIncluded++;
  }
  return summary;
}

/* --------------------------------------------------------
  DETERMINE OPTIMISTIC ACTION FROM PAYLOAD DIFF
-------------------------------------------------------- */

interface OptimisticAction {
  type: "exclude" | "include";
  userAccountId: number;
  mode: string;
}

function detectOptimisticAction(
  payload: UpdatePayload,
  currentSettings:
    | {
        excludedUsers: { userAccountId: number }[];
        includedUsers: { userAccountId: number }[];
      }
    | undefined
): OptimisticAction | null {
  if (!currentSettings) return null;

  const currentExcludedIds = new Set(
    currentSettings.excludedUsers.map((u) => u.userAccountId)
  );
  const currentIncludedIds = new Set(
    currentSettings.includedUsers.map((u) => u.userAccountId)
  );
  const payloadExcludedIds = new Set(payload.excludedUserAccountIds);
  const payloadIncludedIds = new Set(payload.includedUserAccountIds);

  // Check for newly excluded user
  for (const id of payload.excludedUserAccountIds) {
    if (!currentExcludedIds.has(id)) {
      return { type: "exclude", userAccountId: id, mode: payload.mode };
    }
  }

  // Check for removed exclusion (include action in all_who_qualify)
  for (const id of currentExcludedIds) {
    if (!payloadExcludedIds.has(id)) {
      return { type: "include", userAccountId: id, mode: payload.mode };
    }
  }

  // Check for newly included user
  for (const id of payload.includedUserAccountIds) {
    if (!currentIncludedIds.has(id)) {
      return { type: "include", userAccountId: id, mode: payload.mode };
    }
  }

  // Check for removed inclusion (exclude action in specific_people)
  for (const id of currentIncludedIds) {
    if (!payloadIncludedIds.has(id)) {
      return { type: "exclude", userAccountId: id, mode: payload.mode };
    }
  }

  return null;
}

/* --------------------------------------------------------
  API DEFINITION
-------------------------------------------------------- */

export const weeklyCheckInReportSettingsApi = createApi({
  reducerPath: "weeklyCheckInReportSettingsApi",
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  baseQuery: fetchBaseQuery({
    baseUrl: `${REACT_APP_API_URL}/talentInsights`,
    prepareHeaders: (headers) => {
      const token = getUserToken();
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: [
    "SeniorManagerSettings",
    "SeniorManagerPreview",
    "TeamLeaderSettings",
    "TeamLeaderPreview",
  ],
  endpoints: (builder) => ({
    /* ─── Senior Manager ─── */

    getSeniorManagerSettings: builder.query<SeniorManagerReportSettings, void>({
      query: () => "seniorManagerReportSettings",
      providesTags: ["SeniorManagerSettings"],
    }),

    updateSeniorManagerSettings: builder.mutation<
      SeniorManagerReportSettings,
      UpdatePayload
    >({
      query: (payload) => ({
        url: "seniorManagerReportSettings",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["SeniorManagerPreview"],
      async onQueryStarted(payload, { dispatch, getState, queryFulfilled }) {
        // Detect what changed to apply optimistic update
        const currentSettings =
          weeklyCheckInReportSettingsApi.endpoints.getSeniorManagerSettings.select()(
            getState() as RootState
          )?.data;

        const action = detectOptimisticAction(payload, currentSettings);

        const includedUserIds = new Set(
          currentSettings?.includedUsers.map((u) => u.userAccountId) ?? []
        );

        let patchResult;
        if (action) {
          patchResult = dispatch(
            weeklyCheckInReportSettingsApi.util.updateQueryData(
              "getSeniorManagerPreview",
              undefined,
              (draft) => {
                if (action.type === "exclude") {
                  draft.recipients = optimisticallyExcludeRecipient(
                    draft.recipients,
                    action.userAccountId,
                    action.mode
                  ) as SeniorManagerReportPreviewRecipient[];
                } else {
                  draft.recipients = optimisticallyIncludeRecipient(
                    draft.recipients,
                    action.userAccountId,
                    includedUserIds
                  ) as SeniorManagerReportPreviewRecipient[];
                }
                draft.summary = recalculateSummary(draft.recipients);
              }
            )
          );
        }

        try {
          const { data } = await queryFulfilled;
          dispatch(
            weeklyCheckInReportSettingsApi.util.updateQueryData(
              "getSeniorManagerSettings",
              undefined,
              () => data
            )
          );
        } catch {
          patchResult?.undo();
        }
      },
    }),

    getSeniorManagerPreview: builder.query<SeniorManagerReportPreview, void>({
      query: () => "seniorManagerReportPreview",
      providesTags: ["SeniorManagerPreview"],
    }),

    /* ─── Team Leader ─── */

    getTeamLeaderSettings: builder.query<TeamLeaderReportSettings, void>({
      query: () => "teamLeaderReportSettings",
      providesTags: ["TeamLeaderSettings"],
    }),

    updateTeamLeaderSettings: builder.mutation<
      TeamLeaderReportSettings,
      UpdatePayload
    >({
      query: (payload) => ({
        url: "teamLeaderReportSettings",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["TeamLeaderPreview"],
      async onQueryStarted(payload, { dispatch, getState, queryFulfilled }) {
        const currentSettings =
          weeklyCheckInReportSettingsApi.endpoints.getTeamLeaderSettings.select()(
            getState() as RootState
          )?.data;

        const action = detectOptimisticAction(payload, currentSettings);

        const includedUserIds = new Set(
          currentSettings?.includedUsers.map((u) => u.userAccountId) ?? []
        );

        let patchResult;
        if (action) {
          patchResult = dispatch(
            weeklyCheckInReportSettingsApi.util.updateQueryData(
              "getTeamLeaderPreview",
              undefined,
              (draft) => {
                if (action.type === "exclude") {
                  draft.recipients = optimisticallyExcludeRecipient(
                    draft.recipients,
                    action.userAccountId,
                    action.mode
                  ) as TeamLeaderReportPreviewRecipient[];
                } else {
                  draft.recipients = optimisticallyIncludeRecipient(
                    draft.recipients,
                    action.userAccountId,
                    includedUserIds
                  ) as TeamLeaderReportPreviewRecipient[];
                }
                draft.summary = recalculateSummary(draft.recipients);
              }
            )
          );
        }

        try {
          const { data } = await queryFulfilled;
          dispatch(
            weeklyCheckInReportSettingsApi.util.updateQueryData(
              "getTeamLeaderSettings",
              undefined,
              () => data
            )
          );
        } catch {
          patchResult?.undo();
        }
      },
    }),

    getTeamLeaderPreview: builder.query<TeamLeaderReportPreview, void>({
      query: () => "teamLeaderReportPreview",
      providesTags: ["TeamLeaderPreview"],
    }),
  }),
});

export const {
  useGetSeniorManagerSettingsQuery,
  useUpdateSeniorManagerSettingsMutation,
  useGetSeniorManagerPreviewQuery,
  useGetTeamLeaderSettingsQuery,
  useUpdateTeamLeaderSettingsMutation,
  useGetTeamLeaderPreviewQuery,
} = weeklyCheckInReportSettingsApi;
